/**
 * Vendor Foundation Sprint — Phase 2 (2026-08-02)
 *
 * /admin/vendors — Admin Vendor Queue
 *
 * List of vendor applications with lifecycle actions:
 * Approve · Reject · Request More Info · Suspend · Reactivate ·
 * Change Plan · Enable Book Talent · Merge/Link · View Audit History.
 *
 * Backend: /api/admin/vendors  (routes/admin_vendor_queue_routes.py)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ExternalLink, Check, X, Ban, HelpCircle, RefreshCw,
  GitMerge, History, Package, Sparkles, ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_BADGES = {
  pending_review: { label: 'Pending Review',  cls: 'bg-amber-100 text-amber-800 ring-amber-200' },
  needs_info:     { label: 'Needs Info',      cls: 'bg-sky-100 text-sky-800 ring-sky-200' },
  approved:       { label: 'Approved',        cls: 'bg-emerald-100 text-emerald-800 ring-emerald-200' },
  rejected:       { label: 'Rejected',        cls: 'bg-rose-100 text-rose-800 ring-rose-200' },
  suspended:      { label: 'Suspended',       cls: 'bg-slate-200 text-slate-800 ring-slate-300' },
  draft:          { label: 'Draft',           cls: 'bg-slate-100 text-slate-700 ring-slate-200' },
};

const TIER_OPTIONS = [
  { value: 'standard',   label: 'Preferred Vendor ($99/mo)' },
  { value: 'plus',       label: 'Premier Vendor ($149/mo)' },
  { value: 'elite',      label: 'Exclusive Vendor ($299/mo)' },
  { value: 'enterprise', label: 'Signature Partner (custom)' },
];

const STATUS_FILTERS = [
  { key: 'pending_review', label: 'Pending' },
  { key: 'needs_info',     label: 'Needs Info' },
  { key: 'approved',       label: 'Approved' },
  { key: 'rejected',       label: 'Rejected' },
  { key: 'suspended',      label: 'Suspended' },
  { key: 'all',            label: 'All' },
];


export default function AdminVendorQueue() {
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openVendorId, setOpenVendorId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', statusFilter);
      if (debouncedQ) params.set('q', debouncedQ);
      const r = await axios.get(`${API}/admin/vendors?${params.toString()}`, AUTH());
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load vendors');
    } finally { setLoading(false); }
  }, [statusFilter, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-[100dvh] bg-slate-50" data-testid="admin-vendor-queue-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Vendor Applications</h1>
              <p className="text-sm text-slate-500">Review, approve, and manage vendor identities across iEvntür.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/vendor-migration'} data-testid="admin-vendor-migration-link">
                <Package className="w-4 h-4 mr-1.5" /> Import Vendors
              </Button>
              <Button variant="outline" size="sm" onClick={load} data-testid="admin-vendor-refresh">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 items-center">
            {STATUS_FILTERS.map(s => (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border ${statusFilter === s.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
                data-testid={`admin-vendor-filter-${s.key}`}
              >
                {s.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <Input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search company, email, ref…"
                className="w-64 h-9"
                data-testid="admin-vendor-search"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="admin-vendor-empty">
            <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            No vendor applications match this filter.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(v => (
              <VendorRow key={v.id} v={v} onOpen={() => setOpenVendorId(v.id)} />
            ))}
          </div>
        )}
      </main>

      {openVendorId && (
        <VendorDetailDrawer
          vendorId={openVendorId}
          onClose={() => setOpenVendorId(null)}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}


function VendorRow({ v, onOpen }) {
  const badge = STATUS_BADGES[v.approval_status] || STATUS_BADGES.draft;
  const markets = v.markets || '—';
  return (
    <Card
      className="p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={onOpen}
      data-testid={`admin-vendor-row-${v.id}`}
    >
      <div className="flex flex-wrap items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
          {v.logo_url ? (
            <img src={v.logo_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <Package className="w-5 h-5 text-slate-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 truncate">{v.company_name}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ring-1 font-medium ${badge.cls}`}>{badge.label}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-mono">{v.application_ref}</span>
            {v.vendor_level_display && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">{v.vendor_level_display}</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1 truncate">
            {v.contact_email || '—'} · {v.category_name || 'No category'} · {markets}
            {v.website_url && (
              <> · <a href={v.website_url} target="_blank" rel="noreferrer" className="text-teal-700 hover:underline inline-flex items-center gap-0.5">website <ExternalLink className="w-3 h-3" /></a></>
            )}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            Source: {v.signup_source || 'in_app'}
            {v.stripe_customer_id && <> · Stripe: {v.vendor_subscription_status || 'no sub'}</>}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-300 mt-3" />
      </div>
    </Card>
  );
}


function VendorDetailDrawer({ vendorId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('overview');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/vendors/${vendorId}`, AUTH());
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load vendor');
    }
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  const call = async (path, body) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/vendors/${vendorId}/${path}`, body || {}, AUTH());
      toast.success(`Action complete: ${path.replace(/-/g, ' ')}`);
      await load();
      onChanged?.();
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed: ${path}`);
    } finally { setBusy(false); }
  };

  const approve = () => window.confirm('Approve this vendor?') && call('approve');
  const reject = () => {
    const reason = window.prompt('Reason for rejection (visible in audit log):');
    if (reason) call('reject', { reason });
  };
  const requestInfo = () => {
    const message = window.prompt('What info do you need from the vendor?');
    if (message) call('request-info', { message });
  };
  const suspend = () => {
    const reason = window.prompt('Suspension reason:');
    if (reason) call('suspend', { reason });
  };
  const reactivate = () => window.confirm('Reactivate this vendor?') && call('reactivate');
  const changePlan = () => {
    const tier = window.prompt(
      'New plan tier?\n\n  standard = Preferred\n  plus     = Premier\n  elite    = Exclusive\n  enterprise = Signature Partner',
      data?.vendor_level || 'standard',
    );
    if (tier && TIER_OPTIONS.some(o => o.value === tier)) call('change-plan', { tier });
  };
  const enableBookTalent = () => call('enable-book-talent');

  const disableCap = (cap) => {
    if (window.confirm(`Disable capability "${cap}"?`)) call('disable-capability', { capability: cap });
  };

  if (!data) {
    return (
      <div className="fixed inset-0 bg-slate-900/40 z-40 flex justify-end" onClick={onClose}>
        <div className="w-full max-w-2xl bg-white h-full flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const badge = STATUS_BADGES[data.approval_status] || STATUS_BADGES.draft;

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-40 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="admin-vendor-detail-drawer"
      >
        <header className="sticky top-0 bg-white border-b p-4 z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-slate-900 truncate">{data.company_name}</h2>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ring-1 font-medium ${badge.cls}`}>{badge.label}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-mono">{data.application_ref}</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {data.owner_email} · {data.vendor_level_display || 'no plan'} · signup: {data.signup_source}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="admin-vendor-drawer-close">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {data.approval_status !== 'approved' && (
              <Button size="sm" onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-vendor-action-approve">
                <Check className="w-3.5 h-3.5 mr-1" /> Approve
              </Button>
            )}
            {data.approval_status === 'approved' && (
              <Button size="sm" variant="outline" onClick={suspend} disabled={busy} className="text-rose-700" data-testid="admin-vendor-action-suspend">
                <Ban className="w-3.5 h-3.5 mr-1" /> Suspend
              </Button>
            )}
            {data.approval_status === 'suspended' && (
              <Button size="sm" onClick={reactivate} disabled={busy} className="bg-slate-800 hover:bg-slate-900" data-testid="admin-vendor-action-reactivate">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reactivate
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={requestInfo} disabled={busy} data-testid="admin-vendor-action-request-info">
              <HelpCircle className="w-3.5 h-3.5 mr-1" /> Request Info
            </Button>
            <Button size="sm" variant="outline" onClick={reject} disabled={busy} className="text-rose-700" data-testid="admin-vendor-action-reject">
              <X className="w-3.5 h-3.5 mr-1" /> Reject
            </Button>
            <Button size="sm" variant="outline" onClick={changePlan} disabled={busy} data-testid="admin-vendor-action-change-plan">
              <Package className="w-3.5 h-3.5 mr-1" /> Change Plan
            </Button>
            <Button size="sm" variant="outline" onClick={enableBookTalent} disabled={busy} data-testid="admin-vendor-action-enable-book-talent">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Enable Book Talent
            </Button>
          </div>

          <div className="flex gap-3 mt-4 border-b -mb-4">
            {['overview', 'capabilities', 'duplicates', 'audit'].map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`text-sm px-1 py-2 border-b-2 ${tab === t ? 'border-slate-900 text-slate-900 font-semibold' : 'border-transparent text-slate-500'}`}
                data-testid={`admin-vendor-tab-${t}`}
              >
                {t === 'overview' ? 'Overview'
                  : t === 'capabilities' ? `Capabilities (${data.capabilities?.length || 0})`
                  : t === 'duplicates' ? `Duplicates (${data.duplicate_candidates?.length || 0})`
                  : `Audit (${data.audit_log?.length || 0})`}
              </button>
            ))}
          </div>
        </header>

        <div className="p-4">
          {tab === 'overview' && <OverviewTab data={data} />}
          {tab === 'capabilities' && <CapabilitiesTab data={data} onDisable={disableCap} />}
          {tab === 'duplicates' && <DuplicatesTab data={data} onMerge={onChanged} onRefresh={load} />}
          {tab === 'audit' && <AuditTab log={data.audit_log || []} />}
        </div>
      </div>
    </div>
  );
}


function OverviewTab({ data }) {
  const rows = [
    ['Application ref', data.application_ref],
    ['Owner email', data.owner_email],
    ['Contact phone', data.contact_phone],
    ['Website', data.website_url],
    ['Instagram', data.instagram],
    ['Markets', [data.city, data.state, data.country].filter(Boolean).join(', ') || '—'],
    ['Plan', data.vendor_level_display || '—'],
    ['Stripe customer', data.stripe_customer_id || '—'],
    ['Stripe subscription', data.vendor_subscription_status || '—'],
    ['Cross-access enabled', data.cross_access_enabled ? 'Yes' : 'No'],
    ['Signup source', data.signup_source],
    ['Created', data.created_at ? new Date(data.created_at).toLocaleString() : '—'],
    ['Published at', data.published_at ? new Date(data.published_at).toLocaleString() : '—'],
  ];
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-b border-slate-100">
            <td className="py-2 pr-4 text-slate-500 whitespace-nowrap align-top w-40">{k}</td>
            <td className="py-2 text-slate-900 break-words">{v || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


function CapabilitiesTab({ data, onDisable }) {
  const caps = data.capabilities || [];
  if (caps.length === 0) return <div className="text-sm text-slate-500 py-6 text-center">No capabilities activated yet.</div>;
  return (
    <div className="space-y-2">
      {caps.map(c => (
        <Card key={c.capability + c.raw_capability} className="p-3 flex items-center justify-between">
          <div>
            <div className="font-medium text-slate-900">{c.capability.replace(/_/g, ' ')}</div>
            <div className="text-xs text-slate-500">
              {c.enabled ? 'Enabled' : 'Disabled'} · activated {c.activated_at ? new Date(c.activated_at).toLocaleDateString() : '—'}
            </div>
          </div>
          {c.enabled && (
            <Button size="sm" variant="outline" onClick={() => onDisable(c.capability)} className="text-rose-700" data-testid={`admin-vendor-cap-disable-${c.capability}`}>
              Disable
            </Button>
          )}
        </Card>
      ))}
    </div>
  );
}


function DuplicatesTab({ data, onMerge, onRefresh }) {
  const dupes = data.duplicate_candidates || [];
  const [busy, setBusy] = useState(false);

  const doMerge = async (dupCompanyId) => {
    const reason = window.prompt(
      'MERGE this duplicate company INTO the current vendor?\n\n' +
      'This action:\n' +
      ' • Re-parents all users, vendor profiles, capabilities\n' +
      ' • Preserves Stripe customer / subscriptions / billing history\n' +
      ' • Preserves the earlier signup date\n' +
      ' • Writes a permanent record to company_merge_log\n\n' +
      'Reason (visible in audit trail):',
    );
    if (!reason) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/vendors/merge`, {
        primary_company_id: data.company_id,
        duplicate_company_id: dupCompanyId,
        reason,
      }, AUTH());
      toast.success('Merged successfully');
      onMerge?.();
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally { setBusy(false); }
  };

  if (dupes.length === 0) {
    return <div className="text-sm text-slate-500 py-6 text-center">No duplicate candidates detected.</div>;
  }
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Candidates detected by matching email, phone, website domain, or exact company name.
        Merging is destructive to the duplicate but preserves all Stripe/subscription/billing state on the primary.
      </p>
      {dupes.map(d => (
        <Card key={d.company_id} className="p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-900 truncate">{d.company_name}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {d.owner_email} · {d.website_url || '—'} · {d.contact_phone || '—'}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Status: <span className="font-medium">{d.approval_status || '—'}</span>
                {d.vendor_level && <> · Plan: <span className="font-medium">{d.vendor_level}</span></>}
                {d.stripe_customer_id && <> · <span className="text-emerald-700">Stripe attached</span></>}
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {(d.signals || []).map(s => (
                  <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 ring-1 ring-amber-100 uppercase tracking-wide">match: {s}</span>
                ))}
                {(d.capabilities || []).map(c => (
                  <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">{c}</span>
                ))}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => doMerge(d.company_id)}
              disabled={busy}
              className="bg-fuchsia-700 hover:bg-fuchsia-800"
              data-testid={`admin-vendor-merge-${d.company_id}`}
            >
              <GitMerge className="w-3.5 h-3.5 mr-1" /> Merge / Link
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}


function AuditTab({ log }) {
  if (!log.length) return <div className="text-sm text-slate-500 py-6 text-center">No audit entries yet.</div>;
  return (
    <ol className="relative border-l border-slate-200 ml-3 space-y-4">
      {log.map(e => (
        <li key={e.id} className="ml-5">
          <div className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-slate-300 ring-2 ring-white" />
          <div className="text-sm font-medium text-slate-900 flex items-center gap-2 flex-wrap">
            <History className="w-3.5 h-3.5 text-slate-400" />
            <span>{e.action.replace(/_/g, ' ')}</span>
            {e.previous_status && e.new_status && (
              <span className="text-xs text-slate-500">
                {e.previous_status} → <span className="font-semibold">{e.new_status}</span>
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {e.created_at ? new Date(e.created_at).toLocaleString() : ''}
            {e.actor_email && <> · by {e.actor_email}</>}
          </div>
          {e.reason && <div className="text-xs text-slate-700 mt-1 bg-slate-50 rounded p-2">{e.reason}</div>}
        </li>
      ))}
    </ol>
  );
}
